import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Checkbox, Dialog, Label, Select, Text, TextArea, TextInput, useToaster } from '@gravity-ui/uikit';
import type { ManagerBonusView, ManagerCardResponse, ManagerView } from '@odonix/contracts';
import { useTenantsApi } from '../../lib/api/context.js';
import { useManagersApi } from '../../lib/api/managers-context.js';
import { TenantsError } from '../../lib/api/tenants.js';
import { Field } from '../../ui/field.js';
import { day, monthTitle, parseRubles, percent, rub } from '../../ui/format.js';
import { addMonths, mskToday } from '../../ui/period.js';
import { LoadingRows } from '../../ui/states.js';
import { STATE_LABEL, STATE_TONE } from '../tenants/labels.js';
import { DateInput } from '../../ui/date-input.js';

/**
 * Окна действий над менеджером. Каждое — одно решение с понятным следствием:
 * ставка «с какого дня», премия «за какой месяц и за что», удаление — «кому
 * достаются его клиники».
 */

function useRefresh(managerId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['manager-card', managerId] }),
      queryClient.invalidateQueries({ queryKey: ['manager-earnings', managerId] }),
      queryClient.invalidateQueries({ queryKey: ['managers-overview'] }),
      queryClient.invalidateQueries({ queryKey: ['managers'] }),
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] }),
      queryClient.invalidateQueries({ queryKey: ['tenants'] }),
      queryClient.invalidateQueries({ queryKey: ['manager-audit'] }),
    ]);
  };
}

function errorProps(error: unknown) {
  if (error === null || error === undefined) return {};
  return {
    errorText: error instanceof TenantsError ? error.message : 'Не удалось выполнить действие. Повторите',
    showError: true,
  };
}

interface BaseProps {
  open: boolean;
  onClose: () => void;
}

// --- Процент ----------------------------------------------------------------

export function CommissionDialog({ open, onClose, card }: BaseProps & { card: ManagerCardResponse }) {
  const api = useManagersApi();
  const refresh = useRefresh(card.manager.id);
  const toaster = useToaster();
  const [rate, setRate] = useState(String(card.manager.commissionPercent).replace('.', ','));
  const [from, setFrom] = useState(mskToday());

  const normalized = rate.trim().replace(',', '.');
  const value = /^\d{1,3}(\.\d{1,2})?$/.test(normalized) ? Number(normalized) : null;
  const valid = value !== null && value <= 100 && from !== '';

  const mutation = useMutation({
    mutationFn: () => api.setCommission(card.manager.id, { percent: value ?? 0, effectiveFrom: from }),
    onSuccess: async () => {
      await refresh();
      toaster.add({ name: 'commission', title: `Процент — ${percent(value ?? 0)} с ${day(from)}`, theme: 'success', autoHiding: 4000 });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} size="s" aria-label="Процент от подписки">
      <Dialog.Header caption="Процент от подписки" />
      <Dialog.Body>
        <div className="flex flex-col gap-4">
          <Text color="secondary">
            Начисляется с каждой оплаты подписки клиник менеджера за вычетом возвратов. Новая ставка действует с
            выбранного дня и перекрывает всё, что стояло с этого дня; оплаты раньше остаются при своей ставке.
          </Text>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Процент" required error={value === null || value > 100 ? 'От 0 до 100, не точнее сотых' : undefined}>
              {(id) => (
                <TextInput
                  id={id}
                  value={rate}
                  onUpdate={setRate}
                  autoFocus
                  endContent={<span className="pr-2 text-[var(--g-color-text-secondary)]">%</span>}
                  controlProps={{ inputMode: 'decimal' }}
                />
              )}
            </Field>
            <Field label="Действует с" required>
              {(id) => <DateInput id={id} value={from} onUpdate={setFrom} />}
            </Field>
          </div>
          {card.commissionHistory.length === 0 ? null : (
            <div className="flex flex-col gap-1.5">
              <Text variant="subheader-1">История</Text>
              {card.commissionHistory.slice(0, 6).map((row) => (
                <Text key={row.createdAt} color="secondary" className="tabular">
                  {`${percent(row.percent)} с ${day(row.effectiveFrom)} · поставил ${row.setByEmail ?? '—'} ${day(row.createdAt)}`}
                </Text>
              ))}
            </div>
          )}
        </div>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply="Сохранить"
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ disabled: !valid }}
        {...errorProps(mutation.error)}
      />
    </Dialog>
  );
}

// --- Премия -----------------------------------------------------------------

function recentMonths(count: number): string[] {
  const current = `${mskToday().slice(0, 7)}-01`;
  return Array.from({ length: count }, (_, index) => addMonths(current, -index));
}

export function BonusDialog({ open, onClose, manager }: BaseProps & { manager: ManagerView }) {
  const api = useManagersApi();
  const refresh = useRefresh(manager.id);
  const toaster = useToaster();
  const months = useMemo(() => recentMonths(12), []);
  const [rubles, setRubles] = useState('');
  const [month, setMonth] = useState(months[0] ?? '');
  const [comment, setComment] = useState('');
  const amountMinor = parseRubles(rubles);
  const valid = amountMinor !== null && amountMinor > 0 && comment.trim().length >= 3;

  const mutation = useMutation({
    mutationFn: () => api.addBonus(manager.id, { amountMinor: amountMinor ?? 0, month, comment: comment.trim() }),
    onSuccess: async (bonus) => {
      await refresh();
      toaster.add({
        name: 'bonus',
        title: `Премия ${rub(bonus.amountMinor)} за ${monthTitle(bonus.month).toLowerCase()}`,
        theme: 'success',
        autoHiding: 4000,
      });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} size="s" aria-label="Премия менеджеру">
      <Dialog.Header caption={`Премия: ${manager.fullName}`} />
      <Dialog.Body>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Сумма, ₽" required error={rubles !== '' && amountMinor === null ? 'Число, например 10 000' : undefined}>
              {(id) => (
                <TextInput id={id} value={rubles} onUpdate={setRubles} autoFocus controlProps={{ inputMode: 'decimal' }} />
              )}
            </Field>
            <Field label="За месяц" required>
              {(id) => (
                <Select
                  id={id}
                  width="max"
                  value={[month]}
                  onUpdate={(values) => {
                    setMonth(values[0] ?? month);
                  }}
                  options={months.map((value) => ({ value, content: monthTitle(value) }))}
                />
              )}
            </Field>
          </div>
          <Field label="За что" required hint="Увидит менеджер в своих начислениях.">
            {(id) => (
              <TextArea
                id={id}
                value={comment}
                onUpdate={setComment}
                minRows={2}
                placeholder="Например: десять новых клиник за месяц"
              />
            )}
          </Field>
        </div>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply="Назначить"
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ disabled: !valid }}
        {...errorProps(mutation.error)}
      />
    </Dialog>
  );
}

export function CancelBonusDialog({
  open,
  onClose,
  managerId,
  bonus,
}: BaseProps & { managerId: string; bonus: ManagerBonusView }) {
  const api = useManagersApi();
  const refresh = useRefresh(managerId);
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.cancelBonus(managerId, bonus.id, { reason: reason.trim() }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} size="s" aria-label="Отменить премию">
      <Dialog.Header caption="Отменить премию" />
      <Dialog.Body>
        <div className="flex flex-col gap-4">
          <Text color="secondary">
            {`${rub(bonus.amountMinor)} за ${monthTitle(bonus.month).toLowerCase()} — «${bonus.comment}». Премия не удаляется, а остаётся в истории отменённой.`}
          </Text>
          <Field label="Причина отмены" required>
            {(id) => <TextArea id={id} value={reason} onUpdate={setReason} minRows={2} autoFocus />}
          </Field>
        </div>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply="Отменить премию"
        textButtonCancel="Не отменять"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ disabled: reason.trim().length < 3, view: 'outlined-danger' }}
        {...errorProps(mutation.error)}
      />
    </Dialog>
  );
}

// --- Отключение и удаление ---------------------------------------------------

export function ToggleDialog({ open, onClose, manager }: BaseProps & { manager: ManagerView }) {
  const api = useManagersApi();
  const refresh = useRefresh(manager.id);
  const disabling = manager.status === 'active';

  const mutation = useMutation({
    mutationFn: () => api.update(manager.id, { status: disabling ? 'disabled' : 'active' }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} size="s" aria-label={disabling ? 'Отключить менеджера' : 'Включить менеджера'}>
      <Dialog.Header caption={disabling ? 'Отключить менеджера' : 'Включить менеджера'} />
      <Dialog.Body>
        <Text color="secondary">
          {disabling
            ? 'Открытые сессии закроются сразу, код входа больше не придёт. Клиники, ссылки и начисления остаются за ним — включить обратно можно в любой момент.'
            : 'Менеджер снова сможет входить в кабинет по коду на почту.'}
        </Text>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply={disabling ? 'Отключить' : 'Включить'}
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ view: disabling ? 'outlined-danger' : 'action' }}
        {...errorProps(mutation.error)}
      />
    </Dialog>
  );
}

const NOBODY = '__nobody';

export function DeleteDialog({
  open,
  onClose,
  card,
  onDeleted,
}: BaseProps & { card: ManagerCardResponse; onDeleted: () => void }) {
  const api = useManagersApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();
  const others = useQuery({ queryKey: ['managers'], queryFn: () => api.list(), enabled: open });
  const candidates = (others.data ?? []).filter((manager) => manager.id !== card.manager.id);
  const [target, setTarget] = useState<string>(NOBODY);
  const tenants = card.tenants.length;

  const mutation = useMutation({
    mutationFn: () => api.remove(card.manager.id, target === NOBODY ? null : target),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries();
      toaster.add({
        name: 'manager-deleted',
        title: `${result.manager.fullName} удалён`,
        content:
          result.movedTenants === 0
            ? undefined
            : target === NOBODY
              ? `Клиники (${String(result.movedTenants)}) остались без менеджера.`
              : `Клиники (${String(result.movedTenants)}) переданы.`,
        theme: 'success',
        autoHiding: 5000,
      });
      onDeleted();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} size="s" aria-label="Удалить менеджера">
      <Dialog.Header caption={`Удалить: ${card.manager.fullName}`} />
      <Dialog.Body>
        <div className="flex flex-col gap-4">
          <Text color="secondary">
            Менеджер больше не войдёт, его ссылки перестанут закреплять клиники. Начисления и журнал остаются — это
            история денег. Его почту можно будет завести заново.
          </Text>
          {tenants === 0 ? (
            <Text>Клиник за менеджером нет.</Text>
          ) : (
            <Field label={`Кому передать клиники (${String(tenants)})`} hint="Процент с их будущих оплат начисляется новому менеджеру.">
              {(id) => (
                <Select
                  id={id}
                  width="max"
                  value={[target]}
                  onUpdate={(values) => {
                    setTarget(values[0] ?? NOBODY);
                  }}
                  options={[
                    { value: NOBODY, content: 'Никому — оставить без менеджера' },
                    ...candidates.map((manager) => ({
                      value: manager.id,
                      content: manager.status === 'disabled' ? `${manager.fullName} (отключён)` : manager.fullName,
                    })),
                  ]}
                />
              )}
            </Field>
          )}
        </div>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply="Удалить менеджера"
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ view: 'outlined-danger' }}
        {...errorProps(mutation.error)}
      />
    </Dialog>
  );
}

// --- Закрепление клиник ---------------------------------------------------------

export function AssignTenantsDialog({ open, onClose, manager }: BaseProps & { manager: ManagerView }) {
  const tenantsApi = useTenantsApi();
  const managersApi = useManagersApi();
  const refresh = useRefresh(manager.id);
  const toaster = useToaster();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const free = useQuery({
    queryKey: ['tenants', 'unassigned'],
    queryFn: () => tenantsApi.list({ managerId: 'none', limit: 100 }),
    enabled: open,
  });
  const needle = q.trim().toLowerCase();
  const rows = (free.data?.tenants ?? []).filter(
    (row) =>
      needle === '' ||
      row.name.toLowerCase().includes(needle) ||
      (row.ownerEmail?.toLowerCase().includes(needle) ?? false),
  );

  const mutation = useMutation({
    mutationFn: () => managersApi.assignTenants(manager.id, [...selected]),
    onSuccess: async (result) => {
      await refresh();
      toaster.add({
        name: 'assigned',
        title: `Закреплено клиник: ${String(result.assigned)}`,
        theme: 'success',
        autoHiding: 4000,
      });
      onClose();
    },
  });

  function toggle(id: string, on: boolean): void {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  return (
    <Dialog open={open} onClose={onClose} size="m" aria-label="Закрепить клиники">
      <Dialog.Header caption={`Закрепить клиники за ${manager.fullName}`} />
      <Dialog.Body>
        <div className="flex flex-col gap-3">
          <Text color="secondary">
            Здесь клиники без менеджера. Процент начисляется с их оплат с момента закрепления.
          </Text>
          <TextInput value={q} onUpdate={setQ} placeholder="Название или почта владельца" hasClear autoFocus />
          {free.isPending ? (
            <LoadingRows rows={5} height={36} />
          ) : rows.length === 0 ? (
            <Text color="secondary">{needle === '' ? 'Свободных клиник нет — все за менеджерами.' : 'Ничего не нашлось.'}</Text>
          ) : (
            <ul className="flex max-h-80 flex-col overflow-y-auto rounded-lg border border-[var(--g-color-line-generic)]">
              {rows.map((row) => (
                <li key={row.id} className="border-b border-[var(--g-color-line-generic)] last:border-b-0">
                  <div className="flex items-center justify-between gap-3 px-3 py-2">
                    <Checkbox
                      checked={selected.has(row.id)}
                      onUpdate={(on) => {
                        toggle(row.id, on);
                      }}
                      content={
                        <span className="flex flex-col">
                          <Text>{row.name}</Text>
                          <Text variant="caption-2" color="secondary">
                            {row.ownerEmail ?? 'без владельца'} · с {day(row.createdAt)}
                          </Text>
                        </span>
                      }
                    />
                    <Label theme={STATE_TONE[row.subscriptionState]} size="xs">
                      {STATE_LABEL[row.subscriptionState]}
                    </Label>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply={selected.size === 0 ? 'Закрепить' : `Закрепить (${String(selected.size)})`}
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ disabled: selected.size === 0 }}
        {...errorProps(mutation.error)}
      />
    </Dialog>
  );
}
